// Pathfinder.js

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import { fetchTrainingPathway } from '../api/trainingPathwayApi';
import { fetchAllPathfinderIMTDomainScores } from '../api/pathfinderIMTDomainScoreApi';
import { fetchAllPathfinderCSTDomainScores } from '../api/pathfinderCSTDomainScoreApi';
import { fetchAllPathfinderACCSDomainScores } from '../api/pathfinderACCSDomainScoreApi';
import { fetchAllPathfinderNeuroSurgDomainScores } from '../api/pathfinderNeuroSurgDomainScoreApi';
import { fetchAllPathfinderRadiologyDomainScores } from '../api/pathfinderRadiologyDomainScoreApi'; // Add this import
import { generateClient } from 'aws-amplify/api';
import { listUserProfiles } from '../graphql/queries';
import PathfinderIntroOverlay from '../components/features/PathfinderModules/PathfinderIntroOverlay';
import OnboardingProgressBar from '../components/common/OnboardingProgressBar';

// Import icons that might be used
import { 
  FaUserMd, 
  FaGraduationCap, 
  FaChalkboardTeacher,
  FaClipboardList,
  FaChartLine,
  FaBook,
  FaCertificate,
  FaUndo,
  FaCog,
  FaHospitalUser,
  FaBriefcaseMedical,
  FaHandHoldingMedical,
  FaNotesMedical,
  FaAward,
  FaRegLightbulb,
  FaUsers,
  FaDesktop,
  FaFile,
  FaMedal,
  FaLaptopMedical
} from 'react-icons/fa';

// Import modules
import DailyStreak from '../components/features/PathfinderModules/DailyStreak';
import StageProgression from '../components/features/PathfinderModules/StageProgression';
import AskADoctor from '../components/features/PathfinderModules/AskADoctor';
import CalendarBox from '../components/features/PathfinderModules/CalendarBox';
import SpecialtyBox from '../components/features/PathfinderModules/SpecialtyBox';
import TaskTips from '../components/features/PathfinderModules/TaskTips';
import PathfinderJourneyStageProgressionBoxes from '../components/features/PathfinderModules/PathfinderJourneyStageProgressionBoxes';
import MiddleView from '../components/features/PathfinderModules/MiddleView';
import HorizonView from '../components/features/PathfinderModules/HorizonView';

// Removed SubscriptionGate import

//
// Example stage data
//
const ALL_STAGES = [
  {
    id: 'stage1',
    title: 'Jan 17th: Identify next Cardiology Placement',
    items: ['Placeholder item 1 in Stage 1','Placeholder item 2 in Stage 1'],
  },
  {
    id: 'stage2',
    title: 'Jan 18th: Consultant Cardiologists',
    items: [
      'Find 2-3 Consultant Cardiologists',
      'Ask if they have any current Audits',
      'Identify highest-likelihood Audit',
    ],
  },
  {
    id: 'stage3',
    title: 'Jan 19th: Confirm First Author',
    items: ['Confirm you would be First Author'],
  },
  {
    id: 'stage4',
    title: 'Jan 20th: Some Future Step',
    items: ['Future step 1', 'Future step 2'],
  },
  {
    id: 'stage5',
    title: 'Jan 21st: Another Future Step',
    items: ['Future step 3'],
  },
];

function convertToStageData(stages) {
  return stages.map((st, index) => {
    let color = 'gray';
    if (index === 0) color = 'green'; // completed
    else if (index === 1) color = 'blue'; // active

    const tasks = st.items.map((txt) => ({ text: txt, done: false }));
    return { ...st, color, tasks };
  });
}

const Pathfinder = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [stages, setStages] = useState(() => convertToStageData(ALL_STAGES));
  const [showOverview, setShowOverview] = useState(false);
  const [showHorizon, setShowHorizon] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [categories, setCategories] = useState([]);
  const [trainingPath, setTrainingPath] = useState('IMT'); // Default to IMT
  const [isCheckingPathway, setIsCheckingPathway] = useState(true); // New state to track pathway check
  const [expandedContainer, setExpandedContainer] = useState(null);
  const [stageTaskData, setStageTaskData] = useState(null); // NEW: holds substep details for modal
  const [downloadTriggerCounter, setDownloadTriggerCounter] = useState(0); // NEW
  const [showIntro, setShowIntro] = useState(false);
  const [showOnboardingBar, setShowOnboardingBar] = useState(false);
  const [hideOnboardingBar, setHideOnboardingBar] = useState(false);
  const client = generateClient();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const dismissed = window.localStorage.getItem('pf-intro-dismissed');
      if (!dismissed) setShowIntro(true);
    } catch (_) {
      setShowIntro(true);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isCheckingPathway) return;

    const key = 'pf-onboarding-bar-dismissed';
    let dismissed = false;
    try {
      dismissed = !!window.localStorage.getItem(key);
    } catch (_) {}

    if (dismissed) return;

    setShowOnboardingBar(true);

    const t1 = setTimeout(() => setHideOnboardingBar(true), 3000);
    const t2 = setTimeout(() => {
      setShowOnboardingBar(false);
      try {
        window.localStorage.setItem(key, '1');
      } catch (_) {}
    }, 3400);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [isCheckingPathway]);

  // Map of category icons for mapping string names to components
  const iconComponentMap = {
    "MRCP(UK)": FaBook,
    "Postgraduate Degrees": FaGraduationCap,
    "Presentations/Posters": FaDesktop,
    "Publications": FaFile,
    "Quality Improvement": FaRegLightbulb,
    "Teaching Experience": FaChalkboardTeacher,
    "Training in Teaching": FaUsers,
    "MRCS": FaBook,
    "Other Qualifications": FaCertificate,
    "Surgical Experience": FaBriefcaseMedical,
    "Surgical Skills": FaLaptopMedical,
    "Audit and Quality Improvement": FaClipboardList,
    "Research": FaChartLine,
    "Teaching": FaChalkboardTeacher,
    "Leadership and Management": FaUserMd,
    "Academia": FaGraduationCap,
    "Additional Achievements": FaMedal,
    "default": FaAward
  };
  
  useEffect(() => {
    // Check if we have specialty info from location state (e.g., from DreamSpecialty)
    if (location.state?.selectedSpecialty) {
      setSelectedSpecialty(location.state.selectedSpecialty);
    } else {
      // If no specialty is provided, check if user has one saved
      // This would typically come from a user profile API call
      // For now, we'll default to a placeholder
      setSelectedSpecialty('Cardiology');
    }
    
    // If coming from setup with categories data
    if (location.state?.categories) {
      // Convert string icon names to actual React components
      const processedCategories = location.state.categories.map(cat => ({
        ...cat,
        icon: iconComponentMap[cat.iconName] || iconComponentMap.default
      }));
      
      setCategories(processedCategories);
    }
    
  }, [location.state]);

  // Load user's saved training path (with profile gate)
  useEffect(() => {
    const loadSavedTrainingPath = async () => {
      try {
        setIsCheckingPathway(true);
        const user = await getCurrentUser();
        if (!user) {
          navigate('/dream-specialty'); // Redirect if no user
          return;
        }
        
        // Profile completeness gate
        let chosenSpecialty = '';
        try {
          const { data } = await client.graphql({
            query: listUserProfiles,
            variables: { filter: { owner: { eq: user.username } } },
            authMode: 'userPool'
          });
          const profile = data?.listUserProfiles?.items?.[0];
          const hasProfile = !!profile;
          const nameOK = !!profile?.name;
          const uniOK = !!profile?.medicalUniversity;
          const yearOK = Number.isInteger(Number(profile?.yearInMedSchool)) && Number(profile?.yearInMedSchool) > 0;
          chosenSpecialty = profile?.chosenSpecialty || '';
          if (!hasProfile || !nameOK || !uniOK || !yearOK) {
            navigate('/account_create', { replace: true });
            return;
          }
          // Enforce chosen specialty before allowing Pathfinder
          if (!chosenSpecialty) {
            navigate('/dream-specialty', { replace: true, state: { from: '/pathfinder' } });
            return;
          }
          // New: enforce completed Pathfinder Setup (trainingPath present in backend)
          const profileTrainingPath = (profile?.trainingPath || '').trim();
          if (!profileTrainingPath) {
            const forwardSpecialty = location.state?.selectedSpecialty || chosenSpecialty;
            navigate('/pathfinder-setup', { replace: true, state: { selectedSpecialty: forwardSpecialty } });
            return;
          }
          // Seed local state with profile training path
          setTrainingPath(profileTrainingPath);
        } catch (e) {
          navigate('/account_create', { replace: true });
          return;
        }
        
        const savedPath = await fetchTrainingPathway(user.username);
        // From setup state
        if (location.state?.trainingPath) {
          setTrainingPath(location.state.trainingPath);
          if (location.state?.categories) {
            setCategories(location.state.categories);
          }
        } 
        // Otherwise, if there's a saved path in the backend, use that
        else if (savedPath) {
          setTrainingPath(savedPath);
          console.log(`Loaded saved training path: ${savedPath}`);
        } 
        // If no path anywhere, redirect forward in flow
        else {
          // No saved path yet; proceed to setup (prefer chosenSpecialty if available)
          const forwardSpecialty = location.state?.selectedSpecialty || chosenSpecialty;
          if (forwardSpecialty) {
            navigate('/pathfinder-setup', { replace: true, state: { selectedSpecialty: forwardSpecialty } });
          } else {
            navigate('/dream-specialty', { replace: true });
          }
          return;
        }
      } catch (err) {
        console.error('Error loading saved training path:', err);
        navigate('/dream-specialty');
      } finally {
        setIsCheckingPathway(false);
      }
    };
    
    loadSavedTrainingPath();
  }, [location.state, navigate]);

  // Load category data
  useEffect(() => {
    const loadCategoryData = async () => {
      // If coming from setup with categories data, use it
      if (location.state?.categories) {
        // Convert string icon names to actual React components
        const processedCategories = location.state.categories.map(cat => ({
          ...cat,
          icon: iconComponentMap[cat.iconName] || iconComponentMap.default,
          color: cat.color || '#6b7280' // Use provided color or default gray
        }));
        
        setCategories(processedCategories);
      }
      // Otherwise, try to load from backend if we have a training path
      else if (trainingPath && !isCheckingPathway) {
        try {
          let savedCategories = [];
          
          // Load from the correct API based on training path
          if (trainingPath === 'IMT') {
            savedCategories = await fetchAllPathfinderIMTDomainScores();
            console.log('Loaded IMT domain scores:', savedCategories);
          } else if (trainingPath === 'CST') {
            savedCategories = await fetchAllPathfinderCSTDomainScores();
            console.log('Loaded CST domain scores:', savedCategories);
          } else if (trainingPath === 'ACCS') {
            savedCategories = await fetchAllPathfinderACCSDomainScores();
            console.log('Loaded ACCS domain scores:', savedCategories);
          } else if (trainingPath === 'Neurosurgery' || trainingPath === 'ST1 Neurosurgery') {
             savedCategories = await fetchAllPathfinderNeuroSurgDomainScores();
             console.log('Loaded Neurosurgery domain scores:', savedCategories);
          } else if (trainingPath === 'Radiology' || trainingPath === 'Clinical Radiology') {
             savedCategories = await fetchAllPathfinderRadiologyDomainScores();
             console.log('Loaded Radiology domain scores:', savedCategories);
          }
          
          if (savedCategories && savedCategories.length > 0) {
            const transformedCategories = savedCategories.map((domain) => ({
              id: domain.domainKey,
              categoryKey: domain.domainKey,
              label: domain.domainKey,
              currentPoints: domain.progress || 0, // Use 'progress' field from backend
              totalPoints: getTotalPointsForDomain(domain.domainKey, trainingPath),
              progress: Math.round((domain.progress / getTotalPointsForDomain(domain.domainKey, trainingPath)) * 100) || 0,
              iconName: 'default',
              icon: iconComponentMap[domain.domainKey] || iconComponentMap.default,
              color: '#6b7280' // Default gray
            }));
            
            setCategories(transformedCategories);
            console.log(`Loaded ${trainingPath} categories from backend:`, transformedCategories);
          } else {
            console.log(`No ${trainingPath} data found in backend`);
            setCategories([]);
          }
        } catch (err) {
          console.error(`Error loading ${trainingPath} category points:`, err);
          setCategories([]);
        }
      }
    };
    
    loadCategoryData();
  }, [location.state, trainingPath, isCheckingPathway]);

  // Helper function to get total points for a domain
  const getTotalPointsForDomain = (domainKey, pathway) => {
    if (pathway === 'IMT') {
      const imtTotals = {
        'MRCP(UK)': 8,
        'Postgraduate Degrees': 4,
        'Presentations/Posters': 6,
        'Publications': 8,
        'Quality Improvement': 4,
        'Teaching Experience': 5,
        'Training in Teaching': 3
      };
      return imtTotals[domainKey] || 8;
    } else if (pathway === 'CST') {
      const cstTotals = {
        'Operative Experience': 8,
        'Surgical Experience': 4,
        'Quality Improvement/Audit': 8,
        'Presentations/Publications': 8,
        'Teaching Experience': 8
      };
      return cstTotals[domainKey] || 8;
    } else if (pathway === 'ACCS') {
      const accsTotals = {
        'MSRA: Clinical Problem Solving': 14,
        'MSRA: Professional Dilemmas': 14,
        'Interview: Commitment to Specialty': 10,
        'Interview: Teamwork & Stress': 10,
        'Interview: Teaching & QI': 10,
        'Interview: EM Knowledge': 10,
        'Interview: Clinical Scenarios': 10
      };
      return accsTotals[domainKey] || 10;
    } else if (pathway === 'Radiology' || pathway === 'Clinical Radiology') {
      const radTotals = {
        'Undergraduate Degrees': 4,
        'Postgraduate Degrees': 4,
        'Additional Achievements (Prizes)': 6,
        'Presentations': 8,
        'Publications': 8,
        'Teaching': 8,
        'Quality Improvement': 8,
        'Leadership & Management': 4,
        'Commitment to Radiology': 6
      };
      return radTotals[domainKey] || 10;
    } else if (pathway === 'Neurosurgery' || pathway === 'ST1 Neurosurgery') {
      const neuroTotals = {
        "Undergraduate Career (research, audit, QI, prizes/awards)": 10,
        "Undergraduate clinical electives and attachments": 3,
        "Higher Degree (full-time original research e.g. PhD/MD/Masters by research; excluding degrees integral to curriculum)": 4,
        "First Degree in Medical Sciences (e.g. BSc/BMedSci/MRes; excluding intercalated degrees as part of medical course)": 3,
        "Other Degrees and Diplomas": 2,
        "Postgraduate Research": 5,
        "Postgraduate Academic Awards and Prizes": 4,
        "Postgraduate Clinical Courses (including neurosurgery-relevant and surgical skills courses)": 4,
        "Publications (accepted for publication as a minimum standard)": 18,
        "Presentations": 8,
        "Clinical Audit and Quality Improvement Projects": 8,
        "Organisational and Leadership Skills (including management experience and understanding of NHS management/resources)": 4,
        "Operative Experience (including validated logbook documentation of surgical exposure)": 15,
        "Teaching (including experience of teaching and communication to a range of audiences)": 10,
        "Personal Statement 1 (demonstrating insight, commitment, understanding of programme, and NHS values)": 0,
        "Surgical Placement Experience in Neurosurgery and Allied Specialties": 15
      };
      return neuroTotals[domainKey] || 10;
    }
    return 100; // Default
  };

  // Handle training path change from SpecialtyBox component
  const handleTrainingPathChange = (newPath) => {
    setTrainingPath(newPath);
    console.log(`Training path updated to: ${newPath}`);
  };

  /**
   * expandedContainer can be:
   *   null => nothing expanded
   *   "dailyStreak", "stageProgression", "askADoctor",
   *   "calendarBox", "specialtyBox", or "taskTips", etc.
   */
  // Find the "blue" (active) stage
  const currentActiveIndex = stages.findIndex(s => s.color === 'blue');

  // Toggle a single task
  const toggleTask = (stageIndex, taskIndex) => {
    const newStages = [...stages];
    const stageCopy = { ...newStages[stageIndex] };
    const tasksCopy = [...stageCopy.tasks];

    tasksCopy[taskIndex] = {
      ...tasksCopy[taskIndex],
      done: !tasksCopy[taskIndex].done,
    };
    stageCopy.tasks = tasksCopy;
    newStages[stageIndex] = stageCopy;

    // If all tasks are done => mark this stage green, next stage blue after delay
    const allDone = tasksCopy.every(t => t.done);
    if (allDone) {
      setStages(newStages); // First update to show all checkboxes checked
      
      // Then after delay, update colors and move to next stage
      setTimeout(() => {
        const finalStages = [...newStages];
        finalStages[stageIndex].color = 'green';
        const nextIndex = stageIndex + 1;
        if (nextIndex < finalStages.length) {
          finalStages[nextIndex].color = 'blue';
        }
        setStages(finalStages);
      }, 500); // 0.5 second delay
      
      return; // Exit early to prevent immediate state update
    }

    setStages(newStages);
  };

  // Revert a completed stage back to active
  const revertStage = (stageIndex) => {
    const newStages = [...stages];
    newStages[stageIndex].color = 'blue';
    newStages[stageIndex].tasks = newStages[stageIndex].tasks.map(task => ({
      ...task,
      done: false,
    }));

    // reset next stage to gray
    if (stageIndex + 1 < newStages.length) {
      newStages[stageIndex + 1].color = 'gray';
    }
    setStages(newStages);
  };

  // Expand / close logic
  const handleExpand = (containerName) => {
    setExpandedContainer(containerName);
  };
  const handleCloseExpanded = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setExpandedContainer(null);
      setStageTaskData(null); // ensure cleared
    }, 300);
  };

  // NEW: open subtask details in central modal
  const openStageTask = (taskData) => {
    setStageTaskData(taskData);
    setExpandedContainer('stageTask');
  };

  // For horizontal sliding of the stage boxes
  const stageWidth = 320;
  let offsetIndex = currentActiveIndex - 1;
  if (offsetIndex < 0) offsetIndex = 0;
  if (offsetIndex > stages.length - 3) {
    offsetIndex = stages.length - 3;
  }
  const translateX = -(offsetIndex * stageWidth);
  const totalWidth = stages.length * stageWidth;

  /**
   * Render the *expanded* container in the center,
   * below the always-visible stage boxes.
   */
  const renderExpandedCenter = () => {
    switch (expandedContainer) {
      case 'askADoctor':
        return (
          <div className="expanded-module doctor-module">
            <AskADoctor
              expandedContainer={expandedContainer}
              onExpand={handleExpand}
              onClose={handleCloseExpanded}
              isExpandedInCenter={true}
            />
          </div>
        );
      case 'dailyStreak':
        return (
          <div className="expanded-module streak-module">
            <DailyStreak
              expandedContainer={expandedContainer}
              onExpand={handleExpand}
              onClose={handleCloseExpanded}
              isExpandedInCenter={true}
            />
          </div>
        );
      case 'stageProgression':
        return (
          <div className="expanded-module stage-module">
            <StageProgression
              expandedContainer={expandedContainer}
              onExpand={handleExpand}
              onClose={handleCloseExpanded}
              isExpandedInCenter={true}
              selectedSpecialty={selectedSpecialty}
            />
          </div>
        );
      case 'calendarBox':
        return (
          <div className="expanded-module calendar-module">
            <CalendarBox
              expandedContainer={expandedContainer}
              onExpand={handleExpand}
              onClose={handleCloseExpanded}
              isExpandedInCenter={true}
            />
          </div>
        );
      case 'specialtyBox':
        return (
          <div className="expanded-module specialty-module">
            <SpecialtyBox
              expandedContainer={expandedContainer}
              onExpand={handleExpand}
              onClose={handleCloseExpanded}
              isExpandedInCenter={true}
            />
          </div>
        );
      case 'taskTips':
        return (
          <div className="expanded-module task-module">
            <TaskTips
              expandedContainer={expandedContainer}
              onExpand={handleExpand}
              onClose={handleCloseExpanded}
              isExpandedInCenter={true}
            />
          </div>
        );
      case 'stageTask': {
        if (!stageTaskData) return null;
        const {
          task,
          displayIndex,
          total,
          color,
          category,
          progress,
          details,
          onComplete,
        } = stageTaskData;

        const fallbackNumber =
          displayIndex ||
          (task && task.match(/^(\d+)\.\s/) ? Number(task.match(/^(\d+)\.\s/)[1]) : (progress || 0) + 1);

        const taskTitle = task ? task.replace(/^\d+\.\s/, '') : 'Task details';
        const info = details || {
          description:
            'This task involves completing specific requirements to demonstrate your competency in this area.',
          requirements: [],
          steps: [],
          comments: [],
          resources: [],
          qa: [],
        };

        return (
          <div className="stage-task-expanded">
            <div className="pftask-header" style={{ borderColor: color }}>
              <div className="pftask-category">
                <span className="pftask-category-label">{category}</span>
                <div className="pftask-progress-indicator">
                  <span>Task {fallbackNumber} of {total}</span>
                  <div className="pftask-progress-bar">
                    <div
                      className="pftask-progress-fill"
                      style={{ width: `${((progress || 0) / total) * 100}%`, backgroundColor: color }}
                    />
                  </div>
                </div>
              </div>
              <h2 className="pftask-title">{taskTitle}</h2>
            </div>

            <div className="pftask-content">
              <div className="pftask-section pftask-description">
                <h3 className="pftask-section-title">Description</h3>
                {info.description && <p>{info.description}</p>}
                {Array.isArray(info.requirements) && info.requirements.length > 0 && (
                  <div className="pftask-requirements">
                    <h4>Requirements:</h4>
                    <ul>{info.requirements.map((r, i) => <li key={i}>{r}</li>)}</ul>
                  </div>
                )}
              </div>

              <div className="pftask-columns">
                <div className="pftask-section pftask-steps">
                  <h3 className="pftask-section-title">Steps to Complete</h3>
                  {(() => {
                    // Local, minimal component just for the checklist
                    const StepsChecklist = ({ steps, storageKey }) => {
                      const [items, setItems] = useState(steps || []);

                      // Load persisted state
                      useEffect(() => {
                        if (typeof window === 'undefined') return;
                        try {
                          const raw = localStorage.getItem(storageKey);
                          if (raw) {
                            const persisted = JSON.parse(raw); // [{key,isCompleted}]
                            const map = {};
                            persisted.forEach(p => { map[p.key] = p.isCompleted; });
                            setItems(prev =>
                              prev.map(s => {
                                const key = (s.id ?? s.text);
                                return map.hasOwnProperty(key)
                                  ? { ...s, isCompleted: map[key] }
                                  : s;
                              })
                            );
                          }
                        } catch (_) { /* silent */ }
                      }, [storageKey]);

                      // Persist helper
                      const persist = (next) => {
                        if (typeof window === 'undefined') return;
                        try {
                          const compact = next.map(s => ({ key: (s.id ?? s.text), isCompleted: !!s.isCompleted }));
                          localStorage.setItem(storageKey, JSON.stringify(compact));
                        } catch (_) { /* silent */ }
                      };

                      const toggle = (index) => {
                        setItems(prev => {
                          const next = prev.map((s,i) => i === index ? { ...s, isCompleted: !s.isCompleted } : s);
                          persist(next);
                          return next;
                        });
                      };

                      return (
                        <ul className="pftask-steps-list">
                          {items.map((s, i) => (
                            <li
                              key={s.id ?? s.text}
                              className={`pftask-step ${s.isCompleted ? 'pftask-completed' : ''}`}
                              onClick={() => toggle(i)}
                              style={{ cursor: 'pointer' }}
                              aria-checked={s.isCompleted}
                              role="checkbox"
                            >
                              <div
                                className="pftask-step-checkbox"
                                style={{
                                  border: `2px solid ${color}`,
                                  background: s.isCompleted ? color : 'transparent',
                                  transition: 'background .2s'
                                }}
                              >
                                {s.isCompleted && (
                                  <svg
                                    width="14" height="14" viewBox="0 0 24 24" fill="none"
                                    stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                                  >
                                    <path d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                              <span>{s.text}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    };

                    const steps = (info.steps || []).map(s => ({ ...s })); // shallow copy
                    const storageKey = `pfTaskSteps:${category}:${taskTitle}`; // stable per task
                    return <StepsChecklist steps={steps} storageKey={storageKey} />;
                  })()}
                </div>

                {/* Comments section hidden */}
                <div
                  className="pftask-section pftask-comments"
                  style={{ display: 'none' }}
                  aria-hidden="true"
                />
              </div>

              <div className="pftask-columns">
                <div className="pftask-section pftask-resources">
                  <h3 className="pftask-section-title">Resources</h3>
                  <ul className="pftask-resources-list">
                    {(info.resources || []).map((res, i) => (
                      <li key={i} className="pftask-resource">
                        <a
                          href={res.link || '#'}
                          className="pftask-resource-link"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <span className="pftask-resource-icon" data-type={res.type} />
                          {res.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pftask-section pftask-qa">
                  <h3 className="pftask-section-title">Questions & Answers</h3>
                  <div className="pftask-qa-list">
                    {(info.qa || []).map((q, i) => (
                      <div key={i} className="pftask-qa-item">
                        <div className="pftask-question"><span>{q.question}</span></div>
                        <div className="pftask-answer"><span>{q.answer}</span></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="pftask-footer">
              <div className="pftask-actions">
                <button
                  className="pftask-action-btn pftask-complete-btn"
                  onClick={() => { try { onComplete && onComplete(); } finally { handleCloseExpanded(); } }}
                >
                  Mark as Complete
                </button>
                <button className="pftask-action-btn pftask-save-btn">Save Progress</button>
              </div>
            </div>
          </div>
        );
      }
      default:
        return null;
    }
  };

  // New modal wrapper for expanded modules
  const renderModalExpandedWindow = () => {
    if (!expandedContainer) return null;
    return (
      <div className={`modal-overlay ${isClosing ? 'closing' : ''}`} onClick={handleCloseExpanded}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={handleCloseExpanded} title="Close">
            ✕
          </button>
          {renderExpandedCenter()}
        </div>
      </div>
    );
  };

  // Show loading screen while checking pathway
  if (isCheckingPathway) {
    return (
      <div className="pathfinder-loading">
        <div className="loading-spinner"></div>
        <p>Loading your training pathway...</p>
        <style jsx>{`
          .pathfinder-loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100vh;
            width: 100vw;
          }
          .loading-spinner {
            border: 4px solid rgba(0, 0, 0, 0.1);
            border-left-color: #007BFF;
            border-radius: 50%;
            width: 40px;
            height: 40px;
            animation: spin 1s linear infinite;
            margin-bottom: 1rem;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <div className="pathfinder-container pf-page">
        {/* Static spacer always present */}
        <div
          className="pf-header-spacer"
          style={{ height: 'calc(var(--header-height, 64px) + 1px)' }}
          aria-hidden="true"
        />
        
        <div className="page-header">
          <div className="header-content">
            <div className="header-title">
              <h2>Pathfinder</h2>
            </div>
            
            {/* New Centered View Switcher */}
            <div className="view-switcher-container">
              <button 
                className={`view-btn horizon-btn ${showHorizon ? 'active' : ''}`}
                onClick={() => { setShowOverview(false); setShowHorizon(true); }}
              >
                Horizon
              </button>
              <button 
                className={`view-btn ${showOverview ? 'active' : ''}`}
                onClick={() => { setShowOverview(true); setShowHorizon(false); }}
              >
                Middle
              </button>
              <button 
                className={`view-btn ${!showHorizon && !showOverview ? 'active' : ''}`}
                onClick={() => { setShowOverview(false); setShowHorizon(false); }}
              >
                Deep
              </button>
            </div>
            
            
          </div>
        </div>

        {showOnboardingBar && (
          <div className={`pf-onboardingBar ${hideOnboardingBar ? 'is-hiding' : ''}`}>
            <OnboardingProgressBar currentStep={4} />
          </div>
        )}

        <div className="pf-layout">
          <aside className="pf-sidebar pf-left">
            <div className="pf-sidebar-inner">
               <DailyStreak
                 expandedContainer={expandedContainer}
                 onExpand={handleExpand}
                 onClose={handleCloseExpanded}
               />
               <StageProgression
                 expandedContainer={expandedContainer}
                 onExpand={handleExpand}
                 onClose={handleCloseExpanded}
                 selectedSpecialty={selectedSpecialty}
               />
               <TaskTips
                 expandedContainer={expandedContainer}
                 onExpand={handleExpand}
                 onClose={handleCloseExpanded}
               />
               <AskADoctor
                 expandedContainer={expandedContainer}
                 onExpand={handleExpand}
                 onClose={handleCloseExpanded}
               />
             </div>
           </aside>

          <main className="pf-main">
             {showOverview ? (
               <MiddleView 
                 trainingPath={trainingPath} 
               />
             ) : !showHorizon ? (
               <div className="content-section">
                 <PathfinderJourneyStageProgressionBoxes
                   stages={stages}
                   toggleTask={toggleTask}
                   revertStage={revertStage}
                   totalWidth={totalWidth}
                   translateX={translateX}
                   trainingPath={trainingPath}
                   onOpenTask={openStageTask} // NEW: wire up modal opener
                 />
               </div>
             ) : (
               <HorizonView />
             )}
           </main>

          <aside className="pf-sidebar pf-right">
            <div className="pf-sidebar-inner">
               <CalendarBox
                 expandedContainer={expandedContainer}
                 onExpand={handleExpand}
                 onClose={handleCloseExpanded}
                 trainingPath={trainingPath}
                 downloadTrigger={downloadTriggerCounter} // NEW
               />
               <SpecialtyBox
                 expandedContainer={expandedContainer}
                 onExpand={handleExpand}
                 onClose={handleCloseExpanded}
                 onTrainingPathChange={handleTrainingPathChange}
                 initialTrainingPath={trainingPath}
               />
             </div>
           </aside>
         </div>
         {renderModalExpandedWindow()}
         <button
          type="button"
          className="pf-helpFab"
          onClick={() => {
            setShowIntro(true);
          }}
          aria-label="What is Pathfinder?"
          title="Pathfinder Overview" // Added tooltip text
          style={{
            position: 'fixed',
            left: 'max(1.25rem, env(safe-area-inset-left, 1.25rem))',
            bottom: 'max(1.25rem, env(safe-area-inset-bottom, 1.25rem))',
            width: 42,
            height: 42,
            borderRadius: 999,
            border: 'none',
            background: '#0f172a',
            color: '#e5e7eb',
            fontWeight: 700,
            fontSize: '1.1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 14px 30px rgba(15, 23, 42, 0.32)',
            cursor: 'pointer',
            zIndex: 2100,
          }}
        >
          ?
        </button>
        <PathfinderIntroOverlay
          open={showIntro}
          onClose={() => setShowIntro(false)}
        />
        {/* STYLES */}
        <style jsx>{`
          /* -----------------------------
             Pathfinder Layout (pf-*)
             ----------------------------- */
           .pf-page {
             width: 100vw;
             min-height: 100vh;
             margin-left: calc(-50vw + 50%);
             margin-right: calc(-50vw + 50%);
             background: var(--color-background);
           }

           /* Single spacer that actually creates top blank space */
           .pf-header-spacer {
             width: 100%;
             height: 0;
             transition: height 0.25s ease;
             background: var(--color-white);
           }

           .pf-layout {
             width: 100%;
             max-width: 1600px;
             margin: 0 auto;
             padding: var(--space-xl);
             display: grid;
             grid-template-columns: clamp(260px, 18vw, 320px) minmax(0, 1fr) clamp(260px, 18vw, 320px);
             gap: var(--space-xl);
             box-sizing: border-box;
           }

           .pf-sidebar {
             position: sticky;
             align-self: start;
             top: calc(var(--header-height) + var(--space-lg));
             height: fit-content;
           }

           .pf-sidebar-inner {
             display: flex;
             flex-direction: column;
             gap: var(--space-md);
           }

           .pf-main {
             min-width: 0;
             display: flex;
             flex-direction: column;
             gap: var(--space-xl);
           }

           /* Scoped info-box overrides for Pathfinder side modules */
           .pf-layout .info-box {
             background: #ffffff;
             border: 1px solid #e5e7eb;
             border-radius: 12px;
             padding: 1rem;
             box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
           }

           .pf-layout .info-box:hover {
             transform: translateY(-1px);
             box-shadow: 0 6px 16px rgba(15, 23, 42, 0.06);
             transition: box-shadow 0.25s ease, transform 0.25s ease;
           }

           .content-section {
             background: #ffffff;
             border: 1px solid #e5e7eb;
             border-radius: 14px;
             padding: clamp(1rem, 2vw, 1.5rem);
             box-shadow: 0 1px 3px rgba(0,0,0,0.05);
           }

           @media (max-width: 1280px) {
             .pf-layout {
               padding: var(--space-lg);
               gap: var(--space-lg);
             }
           }

           @media (max-width: 1024px) {
             .pf-layout {
               grid-template-columns: 1fr;
               display: flex;
               flex-direction: column;
             }
             .pf-sidebar {
               position: static;
               top: auto;
             }
             .pf-left, .pf-right, .pf-main {
               width: 100%;
             }
             
             /* Reorder layout for mobile: Main content first, then Left sidebar (streak etc), then Right sidebar */
             .pf-main {
               order: 1;
             }
             .pf-left {
               order: 2;
             }
             .pf-right {
               order: 3;
             }
           }

           @media (max-width: 640px) {
             .pf-layout {
               padding: var(--space-md);
               gap: var(--space-md);
             }
             .content-section {
               padding: var(--space-md);
             }
             
            
           }

           .pathfinder-container {
             overflow-x: hidden;
             transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
           }

           .page-header {
             position: sticky;
             top: 0;
             z-index: 10;
             background: var(--color-white);
             padding: var(--space-header);
             border-bottom: 1px solid var(--color-border);
             margin-bottom: var(--space-xl);
             box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
             backdrop-filter: blur(8px);
             -webkit-backdrop-filter: blur(8px);
             transition: transform 0.3s ease, box-shadow 0.3s ease;
           }
   
           .header-content {
             width: 100%;
             max-width: 1600px;
             margin: 0 auto;
             padding: 0 var(--space-outer);
             display: flex;
             justify-content: space-between;
             align-items: center;
             gap: 2rem;
             position: relative; /* Added for absolute centering */
           }
   
           .header-title {
             display: flex;
             align-items: baseline;
             gap: 1rem;
             padding-left: calc(var(--space-xl) + 1rem);
           }
   
           .header-title h2 {
             position: relative;
             font-size: 1.625rem;
             font-weight: 600;
             color: var(--color-text);
             margin: 0;
             letter-spacing: -0.02em;
             line-height: 1.2;
             transition: color 0.2s ease;
           }
   
           .header-title h2:after {
             content: '';
             position: absolute;
             bottom: -4px;
             left: 0;
             width: 0;
             height: 2px;
             background: var(--color-primary);
             transition: width 0.2s ease;
           }
   
           .header-title:hover h2:after {
             width: 100%;
           }

           /* New View Switcher Styles */
           .view-switcher-container {
             position: absolute;
             left: 50%;
             transform: translateX(-50%);
             display: flex;
             background: #f1f5f9;
             padding: 4px;
             border-radius: 10px;
             gap: 4px;
           }

           .view-btn {
             padding: 6px 16px;
             border: none;
             background: transparent;
             border-radius: 8px;
             font-size: 0.875rem;
             font-weight: 600;
             color: #64748b;
             cursor: pointer;
             transition: all 0.2s ease;
           }

           .view-btn:hover {
             color: #1e293b;
             background: rgba(255,255,255,0.5);
           }

           .view-btn.active {
             background: #ffffff;
             color: #0f172a;
             box-shadow: 0 1px 3px rgba(0,0,0,0.1);
           }

           @media (max-width: 1024px) {
             .view-switcher-container {
               position: static;
               transform: none;
               order: 3;
               width: 100%;
               justify-content: center;
               margin-top: 0.5rem; /* Reduced from 1rem */
             }
             .header-content {
               flex-direction: column;
               gap: 0.25rem;
               padding: 0.5rem 1rem;
             }
             .header-title {
               padding-left: 0;
               width: 100%;
               justify-content: center;
             }
           }

           @media (max-width: 768px) {
             .horizon-btn {
               display: none;
             }
           }
   
           .header-controls {
             display: flex;
             align-items: center;
             justify-content: flex-end;
             gap: 3rem;
             padding-right: calc(var(--space-xl) + 1rem);
           }
   
           .progress-indicator {
             padding: 0.75rem 1.25rem;
             background: var(--color-white);
             border: 1px solid var(--color-border);
             border-radius: var(--radius-lg);
             box-shadow: 
               0 2px 4px rgba(0, 0, 0, 0.02),
               0 1px 2px rgba(0, 0, 0, 0.03);
             transition: all 0.2s ease;
           }
   
           .progress-indicator:hover {
             transform: translateY(-1px);
             box-shadow: 
               0 4px 6px rgba(0, 0, 0, 0.02),
               0 2px 4px rgba(0, 0, 0, 0.03);
             border-color: var(--color-primary-light);
           }

           /* Modal styles */
             .modal-overlay {
               position: fixed;
               top: 0;
               left: 0;
               right: 0;
               bottom: 0;
               backdrop-filter: blur(8px);
               background: rgba(15, 23, 42, 0.4);
               display: flex;
               align-items: center;
               justify-content: center;
               opacity: 0;
               animation: fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
               z-index: 2000; /* ensure above sticky header */
               padding: 1.25rem;
               padding-top: calc(var(--header-height, 64px) + 1.25rem); /* clear the header area */
               will-change: opacity;
             }
             
             .modal-content {
               position: relative;
               width: min(96vw, 1100px);
               background: #fff;
               border-radius: 14px;
               box-shadow: 0 20px 60px rgba(0,0,0,0.2);
               overflow: hidden;
               transform: scale(1);
               animation: scaleIn 0.2s ease-out;
               /* prevent cropping under sticky header; allow internal scroll */
               max-height: calc(100vh - var(--header-height, 64px) - 2.5rem);
               overflow: auto;
               overscroll-behavior: contain;
             }

             .modal-close {
               position: absolute;
               top: 1.5rem;
               right: 1.5rem;
               width: 32px;
               height: 32px;
               display: flex;
               align-items: center;
               justify-content: center;
               background: rgba(203, 213, 225, 0.4);
               border: none;
               border-radius: 8px;
               font-size: 1.25rem;
               cursor: pointer;
               color: #64748b;
               transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
               z-index: 2;
             }

             .modal-close:hover {
               background: rgba(203, 213, 225, 0.6);
               color: #334155;
               transform: scale(1.05);
             }

             .modal-overlay.closing {
               opacity: 0;
             }

             .modal-overlay.closing .modal-content {
               transform: scale(0.95) translateY(10px);
               opacity: 0;
             }

             /* Expanded Module Sizing */
             .expanded-module { 
               padding: 2rem; 
               width: 100%; 
               /* let the content size within modal-content max-height */
               min-height: auto; 
               display: flex; 
               justify-content: center; 
             }
             
             @media (max-width: 768px) {
               .expanded-module {
                 padding: 1rem;
               }
             }
             .doctor-module { max-width: 1200px; }
             .streak-module { max-width: 1100px; }
             .calendar-module { max-width: 1100px; }
             .specialty-module { max-width: 1100px; }
             .task-module { max-width: 1100px; }
             .stage-module { max-width: 1100px; }

             /* Stage substeps (now rendered inside the central modal) */
             .stage-task-expanded { display: flex; flex-direction: column; }
             .pftask-header {
               padding: 2rem 2.5rem;
               border-bottom: 1px solid #e2e8f0;
               position: relative;
               border-left: 6px solid;
               margin-left: -6px;
             }
             .pftask-category { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
             .pftask-category-label { font-size: 0.875rem; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; }
             .pftask-progress-indicator { display: flex; align-items: center; gap: 1rem; font-size: 0.875rem; color: #64748b; }
             .pftask-progress-bar { width: 100px; height: 4px; background: #e2e8f0; border-radius: 2px; overflow: hidden; }
             .pftask-progress-fill { height: 100%; border-radius: 2px; transition: width 0.3s ease; }
             .pftask-title { font-size: 1.5rem; font-weight: 700; color: #1e293b; margin: 0; line-height: 1.3; }
             .pftask-content { padding: 2rem 2.5rem; display: flex; flex-direction: column; gap: 2rem; }
             .pftask-section { display: flex; flex-direction: column; gap: 1rem; }
             .pftask-section-title { display: flex; align-items: center; gap: 0.75rem; font-size: 1.125rem; font-weight: 600; color: #1e293b; margin: 0; padding-bottom: 0.75rem; border-bottom: 1px solid #e2e8f0; }
             .pftask-description p { color: #475569; line-height: 1.6; margin: 0; }
             .pftask-requirements { margin-top: 1rem; padding: 1rem; background: #f8fafc; border-radius: 8px; border: 1px solid #e2e8f0; }
             .pftask-requirements h4 { font-size: 0.875rem; font-weight: 600; color: #1e293b; margin: 0 0 0.75rem 0; }
             .pftask-requirements ul { margin: 0; padding-left: 1.25rem; }
             .pftask-requirements li { color: #475569; margin-bottom: 0.5rem; }
             .pftask-columns { display: flex; gap: 2rem; }
             .pftask-columns .pftask-section { flex: 1; }

             @media (max-width: 768px) {
               .pftask-columns { flex-direction: column; gap: 1.5rem; }
               .pftask-header { padding: 1.25rem 1rem; }
               .pftask-content { padding: 1.25rem 1rem; }
               .pftask-title { font-size: 1.25rem; }
               .pftask-section-title { font-size: 1rem; }
               .modal-content {
                 width: 100%;
                 height: 100%;
                 max-height: 100vh;
                 border-radius: 0;
               }
               .modal-overlay {
                 padding: 0;
                 padding-top: var(--header-height, 64px); 
                 align-items: flex-start;
               }
               .modal-close {
                 top: 1rem;
                 right: 1rem;
               }
             }
             .pftask-steps-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.75rem; }
             .pftask-step { display: flex; align-items: center; gap: 0.75rem; padding: 0.75rem; background: #f8fafc; border-radius: 8px; border-left: 3px solid #e2e8f0; transition: all 0.2s ease; }
             .pftask-step.pftask-completed { border-left-color: #10b981; background: #f0fdf4; }
             .pftask-step-checkbox { width: 20px; height: 20px; border-radius: 4px; border: 2px solid #cbd5e0; display: flex; align-items: center; justify-content: center; color: #10b981; flex-shrink: 0; }
             .pftask-step.pftask-completed .pftask-step-checkbox { background: #10b981; border-color: #10b981; color: white; }
             .pftask-comments-list { display: flex; flex-direction: column; gap: 1rem; }
             .pftask-comment { padding: 1rem; background: #f8fafc; border-radius: 8px; border: 1px solid #e2e8f0; }
             .pftask-comment-header { display: flex; justify-content: space-between; margin-bottom: 0.5rem; }
             .pftask-comment-date { font-size: 0.75rem; color: #94a3b8; }
             .pftask-add-comment { display: flex; gap: 0.5rem; margin-top: 1rem; }
             .pftask-comment-input { flex: 1; padding: 0.75rem 1rem; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 0.875rem; color: #1e293b; }
             .pftask-comment-input:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 2px rgba(59,130,246,0.2); }
             .pftask-comment-btn { padding: 0.75rem 1.25rem; background: #3b82f6; color: white; border: none; border-radius: 8px; font-size: 0.875rem; font-weight: 500; cursor: pointer; transition: all 0.2s ease; }
             .pftask-comment-btn:hover { background: #2563eb; }
             .pftask-resources-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.5rem; }
             .pftask-resource-link { display: flex; align-items: center; gap: 0.75rem; padding: 0.75rem; background: #f8fafc; border-radius: 8px; color: #3b82f6; text-decoration: none; transition: all 0.2s ease; }
             .pftask-resource-link:hover { background: #eff6ff; transform: translateY(-1px); }
             .pftask-resource-icon { display: inline-flex; width: 32px; height: 32px; background: #eff6ff; border-radius: 8px; }
             .pftask-qa-list { display: flex; flex-direction: column; gap: 1rem; }
             .pftask-qa-item { background: #f8fafc; border-radius: 8px; overflow: hidden; border: 1px solid #e2e8f0; }
             .pftask-question { padding: 1rem; background: #f1f5f9; border-bottom: 1px solid #e2e8f0; color: #1e293b; font-weight: 500; }
             .pftask-answer { padding: 1rem; color: #475569; }
             .pftask-footer { padding: 1.5rem 2.5rem; border-top: 1px solid #e2e8f0; background: #f8fafc; border-radius: 0 0 16px 16px; }
             .pftask-actions { display: flex; justify-content: flex-end; gap: 1rem; }
             .pftask-action-btn { padding: 0.75rem 1.5rem; border-radius: 8px; font-size: 0.875rem; font-weight: 500; cursor: pointer; transition: all 0.2s ease; }
             .pftask-complete-btn { background: #3b82f6; color: white; border: none; }
             .pftask-complete-btn:hover { background: #2563eb; transform: translateY(-1px); }
             .pftask-save-btn { background: white; color: #475569; border: 1px solid #e2e8f0; }
             .pftask-save-btn:hover { background: #f1f5f9; transform: translateY(-1px); }
   
           .pf-onboardingBar {
             width: 100%;
             max-width: 1600px;
             margin: 0 auto;
             padding: 0 var(--space-xl);
             margin-top: calc(-1 * var(--space-lg));
             margin-bottom: var(--space-lg);
             box-sizing: border-box;

             opacity: 1;
             transform: translateY(0);
             max-height: 220px;
             transition:
               opacity 380ms ease,
               transform 380ms ease,
               max-height 380ms ease,
               margin-bottom 380ms ease;
             will-change: opacity, transform, max-height;
           }

           .pf-onboardingBar.is-hiding {
             opacity: 0;
             transform: translateY(-10px);
             max-height: 0;
             margin-bottom: 0;
             pointer-events: none;
             overflow: hidden;
           }

           @media (max-width: 1280px) {
             .pf-onboardingBar {
               padding: 0 var(--space-lg);
             }
           }

           @media (max-width: 640px) {
             .pf-onboardingBar {
               padding: 0 var(--space-md);
               margin-top: 0;
             }
           }

           .pf-calgen-btn {
             width: 100%;
             padding: 0.75rem 1rem;
             border: 1px solid #e5e7eb;
             background: #ffffff;
             color: #0f172a;
             font-size: 0.875rem;
             font-weight: 600;
             border-radius: 10px;
             cursor: pointer;
             margin-bottom: 0.5rem;
             display: inline-flex;
             justify-content: center;
             align-items: center;
             gap: 0.5rem;
             transition: background .2s ease, box-shadow .2s ease, transform .2s ease;
           }
           .pf-calgen-btn:hover {
             background: #f8fafc;
             box-shadow: 0 4px 12px rgba(0,0,0,0.05);
             transform: translateY(-2px);
           }
           .pf-calgen-btn:active {
             transform: translateY(0);
             box-shadow: 0 2px 6px rgba(0,0,0,0.04);
           }
           /* @media (prefers-color-scheme: dark) {
             .pf-calgen-btn {
               background: #1e293b;
               color: #f1f5f9;
               border-color: #334155;
             }
             .pf-calgen-btn:hover {
               background: #27364a;
             }
           } */

           /* Bottom-left help FAB */
           .pf-helpFab {
             position: fixed;
             left: max(1.25rem, env(safe-area-inset-left, 1.25rem));
             bottom: max(1.25rem, env(safe-area-inset-bottom, 1.25rem));
             width: 42px;
             height: 42px;
             border-radius: 999px;
             border: none;
             background: #0f172a;
             color: #e5e7eb;
             font-weight: 700;
             font-size: 1.1rem;
             display: flex;
             align-items: center;
             justify-content: center;
             box-shadow: 0 14px 30px rgba(15, 23, 42, 0.32);
             cursor: pointer;
             z-index: 2100;
             transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
           }
           .pf-helpFab:hover {
             transform: translateY(-2px);
             box-shadow: 0 20px 40px rgba(0, 23, 42, 0.4);
             background: #020617;
           }
           .pf-helpFab:active {
             transform: translateY(0);
             box-shadow: 0 10px 24px rgba(0, 23, 42, 0.35);
           }
           /* @media (prefers-color-scheme: dark) {
             .pf-helpFab {
               background: #e5e7eb;
               color: #020617;
             }
           } */
         `}</style>
       </div>
    </>
  );
};

export default Pathfinder;